import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import TemplatePreview from '../webapp/template-editor/TemplatePreview';
import { getPrintSizeLabel } from '../../constants/printSizes';

/**
 * TemplatesOverlaysDisplay Component
 * Displays photo templates and overlays in a tabbed interface
 * Used in event proposals to showcase available templates
 */
const TemplatesOverlaysDisplay = ({ templates = [], overlays = [] }) => {
  const [activeTab, setActiveTab] = useState('templates');
  const allTemplates = useSelector((store) => store.proposalOverlays?.data || {});

  const resolvedTemplates = useMemo(() => {
    return templates
      .map((item) => {
        if (typeof item === 'object' && item.imageUrl) {
          return item;
        }
        return allTemplates[item];
      })
      .filter(Boolean);
  }, [templates, allTemplates]);

  const resolvedOverlays = useMemo(() => {
    return overlays
      .map((item) => {
        // If overlay is enriched with imageUrl, use it directly
        if (typeof item === 'object' && item.imageUrl) {
          return item;
        }
        // If overlay is an object with overlay array, extract template ID from overlay[0]
        if (typeof item === 'object' && item.overlay && item.overlay[0]) {
          return allTemplates[item.overlay[0]];
        }
        // If overlay is an object with just id, look it up
        if (typeof item === 'object' && item.id) {
          return allTemplates[item.id];
        }
        // If overlay is a string (template ID), look it up directly
        return allTemplates[item];
      })
      .filter(Boolean);
  }, [overlays, allTemplates]);

  const activeItems = activeTab === 'templates' ? resolvedTemplates : resolvedOverlays;

  const getSizeLabel = (template) => {
    return template?.printSize ? getPrintSizeLabel(template.printSize) : '';
  };

  return (
    <div className="templates-overlays-display">
      <div className="templates-overlays-display__tabs">
        <button
          className={`templates-overlays-display__tab ${
            activeTab === 'templates' ? 'templates-overlays-display__tab--active' : ''
          }`}
          onClick={() => setActiveTab('templates')}
        >
          Photo Templates
        </button>
        <button
          className={`templates-overlays-display__tab ${
            activeTab === 'overlays' ? 'templates-overlays-display__tab--active' : ''
          }`}
          onClick={() => setActiveTab('overlays')}
        >
          Overlays
        </button>
      </div>

      <div className="templates-overlays-display__grid">
        {activeItems.length === 0 ? (
          <div className="templates-overlays-display__empty">No {activeTab} available</div>
        ) : (
          activeItems.map((item, index) => (
            <div key={item.id || index} className="templates-overlays-display__card">
              <div className="templates-overlays-display__preview">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name || 'Template'} />
                ) : (
                  <TemplatePreview stageWidth={120} stageHeight={120} config={item} />
                )}
              </div>
              <div className="templates-overlays-display__label">{getSizeLabel(item)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TemplatesOverlaysDisplay;
